import React, { useState, useMemo } from 'react';
import { FileDto, DocumentType, DOCUMENT_TYPE_LABELS, getDocumentTypeOptions } from '../../../../../types/file.types';
import { useTranslation } from 'react-i18next';
import { Select, Empty, Modal, Form, Upload, Button, Checkbox, Input, message, Tooltip } from 'antd';
import { User } from '../../../../../types/user.types';
import { 
  UploadOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  ExclamationCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  FileSearchOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import FileService from '../../../../../services/file/file.service';

const { Dragger } = Upload;

interface EmployeesDocumentsProps {
  documents: FileDto[];
  userData: User;
  onDocumentsUpdate?: () => Promise<void>; // Belge listesini yenilemek için async callback
}

const EmployeesDocuments: React.FC<EmployeesDocumentsProps> = ({ documents, userData, onDocumentsUpdate }) => {
  const { t } = useTranslation();
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'available' | 'missing' | DocumentType>('all');
  const [selectedCategory, setSelectedCategory] = useState<DocumentType | 'all'>('all');
  
  // Dosya yükleme state'leri
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  
  // Düzenleme modal state'leri
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingDocument, setEditingDocument] = useState<FileDto | null>(null);
  const [editForm] = Form.useForm();
  const [editFileList, setEditFileList] = useState<any[]>([]);
  const [updating, setUpdating] = useState(false);

  // Önizleme modal state'leri
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileDto | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  
  const fileService = FileService.getInstance();

  const getFileIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'pdf':
        return 'ti ti-file-type-pdf text-danger';
      case 'doc':
      case 'docx':
        return 'ti ti-file-type-doc text-primary';
      case 'xls':
      case 'xlsx':
        return 'ti ti-file-type-xls text-success';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'ti ti-file-type-jpg text-warning';
      default:
        return 'ti ti-file text-secondary';
    }
  };

  const getFileType = (path: string) => {
    return path.split('.').pop()?.toLowerCase() || 'unknown';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Belgeleri kategorilere göre grupla
  const categorizedDocuments = useMemo(() => {
    const categories: Record<DocumentType, FileDto[]> = {} as Record<DocumentType, FileDto[]>;
    const uncategorized: FileDto[] = [];

    // Tüm kategorileri başlat (askerlik durum belgesi sadece erkeklerde)
    Object.values(DocumentType).forEach(type => {
      if (type === DocumentType.MILITARY_SERVICE_CERTIFICATE && userData?.gender !== 'male') {
        return; // Erkek değilse askerlik durum belgesini ekleme
      }
      categories[type] = [];
    });

    // Belgeleri kategorilere ayır
    documents.forEach(doc => {
      if (doc.documentType && Object.values(DocumentType).includes(doc.documentType)) {
        categories[doc.documentType].push(doc);
      } else {
        uncategorized.push(doc);
      }
    });

    return { categories, uncategorized };
  }, [documents, userData?.gender]);

  // Eksik belge tiplerini bul
  const missingDocumentTypes = useMemo(() => {
    return Object.values(DocumentType).filter(type => {
      // Askerlik durum belgesini sadece erkeklerde göster
      if (type === DocumentType.MILITARY_SERVICE_CERTIFICATE) {
        return userData?.gender === 'male' && categorizedDocuments.categories[type].length === 0;
      }
      return categorizedDocuments.categories[type].length === 0;
    });
  }, [categorizedDocuments, userData?.gender]);

  // Filtrelenmiş belgeleri al
  const filteredData = useMemo(() => {
    if (selectedFilter === 'all') {
      return {
        availableCategories: categorizedDocuments.categories,
        missingTypes: missingDocumentTypes,
        uncategorized: categorizedDocuments.uncategorized
      };
    } else if (selectedFilter === 'available') {
      const availableCategories: Record<DocumentType, FileDto[]> = {} as Record<DocumentType, FileDto[]>;
      Object.entries(categorizedDocuments.categories).forEach(([type, docs]) => {
        if (docs.length > 0) {
          availableCategories[type as DocumentType] = docs;
        }
      });
      return {
        availableCategories,
        missingTypes: [],
        uncategorized: categorizedDocuments.uncategorized
      };
    } else if (selectedFilter === 'missing') {
      return {
        availableCategories: {} as Record<DocumentType, FileDto[]>,
        missingTypes: missingDocumentTypes,
        uncategorized: []
      };
    } else {
      // Belirli bir kategori seçildi
      const availableCategories: Record<DocumentType, FileDto[]> = {} as Record<DocumentType, FileDto[]>;
      if (categorizedDocuments.categories[selectedFilter as DocumentType]) {
        availableCategories[selectedFilter as DocumentType] = categorizedDocuments.categories[selectedFilter as DocumentType];
      }
      return {
        availableCategories,
        missingTypes: categorizedDocuments.categories[selectedFilter as DocumentType].length === 0 ? [selectedFilter as DocumentType] : [],
        uncategorized: []
      };
    }
  }, [selectedFilter, categorizedDocuments, missingDocumentTypes]);

  // Kategori badge rengini belirle
  const getCategoryBadgeColor = (type: DocumentType) => {
    const colors: Record<DocumentType, string> = {
      [DocumentType.ID_CARD]: 'processing',
      [DocumentType.ID_PHOTO]: 'success',
      [DocumentType.PASSPORT]: 'purple',
      [DocumentType.DRIVER_LICENSE]: 'cyan',
      [DocumentType.DIPLOMA]: 'gold',
      [DocumentType.CERTIFICATE]: 'lime',
      [DocumentType.HEALTH_REPORT]: 'red',
      [DocumentType.CRIMINAL_RECORD]: 'orange',
      [DocumentType.RESIDENCE]: 'blue',
      [DocumentType.SGK_ENTRY_DOCUMENT]: 'green',
      [DocumentType.EMPLOYMENT_CONTRACT]: 'yellow',
      [DocumentType.MILITARY_SERVICE_CERTIFICATE]: 'pink'
    };
    return colors[type] || 'default';
  };

  // Dosya yükleme modal'ını aç
  const openUploadModal = () => {
    setIsUploadModalVisible(true);
    uploadForm.resetFields();
    setFileList([]);
  };

  // Dosya yükleme modal'ını kapat
  const closeUploadModal = () => {
    setIsUploadModalVisible(false);
    uploadForm.resetFields();
    setFileList([]);
  };

  // Dosya yükleme işlemi
  const handleUploadFile = async (values: any) => {
    if (fileList.length === 0) {
      message.error('Lütfen bir dosya seçin');
      return;
    }

    try {
      setUploading(true);
      const file = fileList[0].originFileObj || fileList[0];

      await fileService.uploadFileForEmployee(
        file,
        userData.id,
        values.fileName,
        values.documentType,
        values.isVisible !== false
      );

      message.success('Dosya başarıyla yüklendi');
      closeUploadModal();
      
      // Belge listesini yenile
      if (onDocumentsUpdate) {
        await onDocumentsUpdate();
      }
    } catch (error) {
      message.error('Dosya yüklenirken hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  // Upload component props
  const uploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    beforeUpload: (file: File) => {
      setFileList([file]);
      return false; // Otomatik yüklemeyi engelle
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  // Düzenleme modal'ını aç
  const openEditModal = (document: FileDto) => {
    setEditingDocument(document);
    setIsEditModalVisible(true);
    editForm.setFieldsValue({
      fileName: document.name,
      documentType: document.documentType,
      isVisible: document.isVisible !== false
    });
    setEditFileList([]);
  };

  // Düzenleme modal'ını kapat
  const closeEditModal = () => {
    setIsEditModalVisible(false);
    setEditingDocument(null);
    editForm.resetFields();
    setEditFileList([]);
  };

  // Belge düzenleme işlemi
  const handleEditDocument = async (values: any) => {
    if (!editingDocument) {
      message.error('Düzenlenecek belge bulunamadı');
      return;
    }

    try {
      setUpdating(true);
      const file = editFileList.length > 0 ? (editFileList[0].originFileObj || editFileList[0]) : undefined;

      await fileService.updateFileForEmployee(
        editingDocument.id,
        values.fileName,
        values.documentType,
        file
      );

      message.success('Belge başarıyla güncellendi');
      closeEditModal();
      
      // Belge listesini yenile
      if (onDocumentsUpdate) {
        await onDocumentsUpdate();
      }
    } catch (error) {
      message.error('Belge güncellenirken hata oluştu');
    } finally {
      setUpdating(false);
    }
  };

  // Edit upload component props
  const editUploadProps = {
    name: 'file',
    multiple: false,
    fileList: editFileList,
    beforeUpload: (file: File) => {
      setEditFileList([file]);
      return false; // Otomatik yüklemeyi engelle
    },
    onRemove: () => {
      setEditFileList([]);
    },
  };

  // Belge silme onayı
  const confirmDeleteDocument = (document: FileDto) => {
    Modal.confirm({
      title: 'Belgeyi Sil',
      icon: <ExclamationCircleOutlined />,
      content: `"${document.name}" belgesi kalıcı olarak silinecek. Bu işlem geri alınamaz. Devam etmek istiyor musunuz?`,
      okText: 'Evet, Sil',
      okType: 'danger',
      cancelText: 'İptal',
      onOk: () => handleDeleteDocument(document),
    });
  };

  // Belge silme işlemi
  const handleDeleteDocument = async (document: FileDto) => {
    try {
      await fileService.deleteFile(document.id);
      message.success('Belge başarıyla silindi');
      
      // Belge listesini yenile
      if (onDocumentsUpdate) {
        await onDocumentsUpdate();
      }
    } catch (error) {
      message.error('Belge silinirken hata oluştu');
    }
  };

  // Dosya görüntüleme (Modal ile)
  const handleView = async (doc: FileDto) => {
    try {
      const blob = await fileService.getFileViewUrl(doc.path);
      
      // Blob boşsa veya hatalıysa
      if (!blob || blob.size === 0) {
        message.error('Dosya bulunamadı');
        return;
      }
      
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewFile(doc);
      setPreviewVisible(true);
    } catch (error: any) {

      if (error?.response?.status === 404) {
        message.error('Dosya bulunamadı');
      } else if (error?.message?.includes('404')) {
        message.error('Dosya bulunamadı');
      } else {
        message.error('Dosya görüntülenirken hata oluştu');
      }
    }
  };

  // Dosya indirme
  const handleDownload = async (doc: FileDto) => {
    try {
      const fileName = doc.name;
      const blob = await fileService.getFileViewUrl(doc.path);
      
      // Blob boşsa veya hatalıysa
      if (!blob || blob.size === 0) {
        message.error('Dosya bulunamadı');
        return;
      }
      
      // Blob'dan Object URL oluştur
      const url = window.URL.createObjectURL(blob);
      
      // Link oluştur ve indir
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Object URL'i temizle
      window.URL.revokeObjectURL(url);
      
      message.success('Dosya indiriliyor');
    } catch (error: any) {

      if (error?.response?.status === 404) {
        message.error('Dosya bulunamadı');
      } else if (error?.message?.includes('404')) {
        message.error('Dosya bulunamadı');
      } else {
        message.error('Dosya indirilirken hata oluştu');
      }
    }
  };

  // Önizleme modalını kapat
  const handleClosePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
    setPreviewVisible(false);
    setPreviewFile(null);
  };

  // Dosyanın görsel olup olmadığını kontrol et
  const isImageFile = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension || '');
  };

  // Görünürlük durumunu değiştir
  const toggleVisibility = async (document: FileDto) => {
    try {
      const newVisibility = !document.isVisible;
      await fileService.toggleFileVisibility(document.id, newVisibility);
      message.success(newVisibility ? 'Belge görünür yapıldı' : 'Belge gizlendi');
      
      // Belge listesini yenile
      if (onDocumentsUpdate) {
        await onDocumentsUpdate();
      }
    } catch (error) {
      message.error('Görünürlük durumu değiştirilirken hata oluştu');
    }
  };

  return (
    <div className="accordion-item">
      <div className="accordion-header" id="headingTwo">
        <div 
          className="accordion-button collapsed"
          data-bs-toggle="collapse"
          data-bs-target="#primaryBorderOne"
          aria-expanded="false"
          aria-controls="primaryBorderOne"
          style={{ cursor: 'pointer' }}
        >
          <div className="d-flex align-items-center justify-content-between flex-fill">
            <div className="d-flex align-items-center">
              <h5 className="mb-0 me-3">{t('employeesDetail.documents.title')}</h5>
            </div>
            <div className="d-flex">
              <i className="ti ti-chevron-down fs-18" />
            </div>
          </div>
        </div>
      </div>
      <div
        id="primaryBorderOne"
        className="accordion-collapse collapse border-top"
        aria-labelledby="headingTwo"
        data-bs-parent="#accordionExample"
      >
        <div className="accordion-body">
          {/* Filtre Kontrolleri ve Belge Ekleme Butonu */}
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label small text-muted">Belge Durumu</label>
              <Select
                style={{ width: '100%' }}
                value={selectedFilter}
                onChange={setSelectedFilter}
                options={[
                  { value: 'all', label: 'Tümü' },
                  { value: 'available', label: 'Mevcut Belgeler' },
                  { value: 'missing', label: 'Eksik Belgeler' },
                  ...getDocumentTypeOptions().filter(option => {
                    // Askerlik durum belgesini sadece erkeklerde göster
                    if (option.value === 'military_service_certificate') {
                      return userData?.gender === 'male';
                    }
                    return true;
                  })
                ]}
              />
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openUploadModal}
                style={{ width: '100%' }}
              >
                Belge Yükle
              </Button>
            </div>
          </div>

          {/* Mevcut Belgeler */}
          {Object.entries(filteredData.availableCategories).map(([type, docs]) => (
            docs.length > 0 && (
              <div key={type} className="mb-4">
                <div className="d-flex align-items-center mb-3">
                  <h6 className="mb-0 me-2">
                    {DOCUMENT_TYPE_LABELS[type as DocumentType]}
                  </h6>
                  <span className="badge bg-light text-dark">{docs.length} belge</span>
                </div>
                <div className="row g-3">
                  {docs.map((doc) => (
                    <div key={doc.id} className="col-sm-6 col-md-4 col-xl-3">
                      <div className="card h-100 shadow-sm hover-shadow-lg transition-all">
                        <div className="card-body p-3">
                          <div className="d-flex flex-column h-100">

                            
                            {/* Üst Kısım - İkon ve İsim */}
                            <div className="document-icon-wrapper mb-3 text-center position-relative">
                              {/* {doc.isVisible === false && (
                                <span className="badge bg-warning text-warning-emphasis position-absolute" style={{ top: '-5px', right: '-5px', fontSize: '0.7rem', padding: '2px 6px' }}>
                                  <i className="ti ti-eye-off" style={{ fontSize: '0.7rem' }}></i>
                                </span>
                              )} */}
                              <i className={`${getFileIcon(getFileType(doc.path))} fs-1`}></i>
                              <h6 className="mt-2 mb-0 text-truncate" style={{ maxWidth: '100%' }} title={doc.name}>
                                {doc.name}
                              </h6>
                            </div>
                            
                            {/* Alt Kısım - Detaylar */}
                            <div className="document-details mt-auto">
                              <div className="d-flex justify-content-between align-items-center text-muted small mb-2">
                                <span>
                                  <i className="ti ti-calendar me-1"></i>
                                  {formatDate(doc.createdAt.toString())}
                                </span>
                                <span>
                                  <i className="ti ti-file-size me-1"></i>
                                  {formatFileSize(doc.size)}
                                </span>
                              </div>
                              
                              {/* İşlem Butonları */}
                              <div className="action-buttons-grid">
                                <Tooltip title="Görüntüle">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<FileSearchOutlined />}
                                    onClick={() => handleView(doc)}
                                  />
                                </Tooltip>
                                <Tooltip title="İndir">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    onClick={() => handleDownload(doc)}
                                  />
                                </Tooltip>
                                <Tooltip title={doc.isVisible ? "Gizle" : "Görünür Yap"}>
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={doc.isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                    onClick={() => toggleVisibility(doc)}
                                    danger={!doc.isVisible}
                                  />
                                </Tooltip>
                                <Tooltip title="Düzenle">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => openEditModal(doc)}
                                  />
                                </Tooltip>
                                <Tooltip title="Sil">
                                  <Button
                                    type="text"
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => confirmDeleteDocument(doc)}
                                  />
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}

          {/* Eksik Belgeler */}
          {filteredData.missingTypes.length > 0 && (
            <div className="mb-4">
              <div className="d-flex align-items-center mb-3">
                <h6 className="text-danger mb-0 me-2">
                  <i className="ti ti-alert-circle me-1"></i>
                  Eksik Belgeler
                </h6>
                <span className="badge bg-danger-subtle text-danger">{filteredData.missingTypes.length} eksik</span>
              </div>
              <div className="row g-3">
                {filteredData.missingTypes.map((type) => (
                  <div key={type} className="col-sm-6 col-md-4 col-xl-3">
                    <div className="card h-100 border-danger border-2 border-dashed">
                      <div className="card-body p-3 text-center">
                        <div className="mb-3">
                          <i className="ti ti-file-off fs-1 text-danger opacity-50"></i>
                        </div>
                        <h6 className="mb-2 text-danger">
                          {DOCUMENT_TYPE_LABELS[type]}
                        </h6>
                        <p className="text-muted small mb-3">Bu belge henüz yüklenmemiş</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Kategorisiz Belgeler */}
          {filteredData.uncategorized.length > 0 && (
            <div className="mb-4">
              <div className="d-flex align-items-center mb-3">
                <h6 className="text-warning mb-0 me-2">
                  <i className="ti ti-question-mark me-1"></i>
                  Kategorisiz Belgeler
                </h6>
                <span className="badge bg-warning-subtle text-warning">{filteredData.uncategorized.length} belge</span>
              </div>
              <div className="row g-3">
                {filteredData.uncategorized.map((doc) => (
                  <div key={doc.id} className="col-sm-6 col-md-4 col-xl-3">
                    <div className="card h-100 shadow-sm hover-shadow-lg transition-all border-warning">
                      <div className="card-body p-3">
                        <div className="d-flex flex-column h-100">

                          
                          {/* Üst Kısım - İkon ve İsim */}
                          <div className="document-icon-wrapper mb-3 text-center">
                            {doc.isVisible === false && (
                              <span className="badge bg-warning-subtle text-warning position-absolute" style={{ top: '8px', right: '8px' }}>
                                <i className="ti ti-eye-off me-1"></i>Gizli
                              </span>
                            )}
                            <i className={`${getFileIcon(getFileType(doc.path))} fs-1`}></i>
                            <h6 className="mt-2 mb-0 text-truncate" style={{ maxWidth: '100%' }} title={doc.name}>
                              {doc.name}
                            </h6>
                          </div>
                          
                          {/* Alt Kısım - Detaylar */}
                          <div className="document-details mt-auto">
                            <div className="d-flex justify-content-between align-items-center text-muted small mb-2">
                              <span>
                                <i className="ti ti-calendar me-1"></i>
                                {formatDate(doc.createdAt.toString())}
                              </span>
                              <span>
                                <i className="ti ti-file-size me-1"></i>
                                {formatFileSize(doc.size)}
                              </span>
                            </div>
                            
                            {/* İşlem Butonları */}
                            <div className="action-buttons-grid">
                              <Tooltip title="Görüntüle">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<FileSearchOutlined />}
                                  onClick={() => handleView(doc)}
                                />
                              </Tooltip>
                              <Tooltip title="İndir">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<DownloadOutlined />}
                                  onClick={() => handleDownload(doc)}
                                />
                              </Tooltip>
                              <Tooltip title={doc.isVisible ? "Gizle" : "Görünür Yap"}>
                                <Button
                                  type="text"
                                  size="small"
                                  icon={doc.isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                  onClick={() => toggleVisibility(doc)}
                                  danger={!doc.isVisible}
                                />
                              </Tooltip>
                              <Tooltip title="Düzenle">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => openEditModal(doc)}
                                />
                              </Tooltip>
                              <Tooltip title="Sil">
                                <Button
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => confirmDeleteDocument(doc)}
                                />
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hiç belge yoksa */}
          {documents.length === 0 && (
            <div className="text-center py-5">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Henüz hiç belge yüklenmemiş"
              />
            </div>
          )}
        </div>
      </div>

      {/* Belge Yükleme Modal'ı */}
      <Modal
        title="Belge Yükle"
        open={isUploadModalVisible}
        onCancel={closeUploadModal}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={uploadForm}
          layout="vertical"
          onFinish={handleUploadFile}
        >
          <Form.Item
            label="Dosya Seç"
            required
          >
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">
                Dosyayı buraya sürükleyin veya tıklayarak seçin
              </p>
              <p className="ant-upload-hint">
                Tek dosya yükleme desteklenir. PDF, DOC, DOCX, JPG, PNG formatları kabul edilir.
              </p>
            </Dragger>
          </Form.Item>

          <Form.Item
            label="Dosya Adı"
            name="fileName"
            rules={[{ required: true, message: 'Lütfen dosya adını girin' }]}
          >
            <Input placeholder="Dosya adını girin..." />
          </Form.Item>

          <Form.Item
            label="Belge Türü"
            name="documentType"
            rules={[{ required: true, message: 'Lütfen belge türünü seçin' }]}
          >
            <Select placeholder="Belge türü seçin...">
              {getDocumentTypeOptions().filter(option => {
                if (option.value === 'military_service_certificate') {
                  return userData?.gender === 'male';
                }
                return true;
              }).map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="isVisible"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox>
              Çalışan tarafından görüntülenebilir
            </Checkbox>
          </Form.Item>

          <div className="d-flex justify-content-end gap-2">
            <Button onClick={closeUploadModal}>
              İptal
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={uploading}
              icon={<UploadOutlined />}
            >
              Dosyayı Yükle
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Belge Düzenleme Modal'ı */}
      <Modal
        title="Belge Düzenle"
        open={isEditModalVisible}
        onCancel={closeEditModal}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditDocument}
        >
          <div className="mb-3 p-3 bg-light rounded">
            <strong>Düzenlenmekte olan belge:</strong> {editingDocument?.name}
          </div>

          <Form.Item
            label="Dosya Adı"
            name="fileName"
            rules={[{ required: true, message: 'Lütfen dosya adını girin' }]}
          >
            <Input placeholder="Dosya adını girin..." />
          </Form.Item>

          <Form.Item
            label="Belge Türü"
            name="documentType"
            rules={[{ required: true, message: 'Lütfen belge türünü seçin' }]}
          >
            <Select placeholder="Belge türü seçin...">
              {getDocumentTypeOptions().filter(option => {
                if (option.value === 'military_service_certificate') {
                  return userData?.gender === 'male';
                }
                return true;
              }).map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Yeni Dosya (İsteğe Bağlı)"
            help="Dosyayı değiştirmek istemiyorsanız boş bırakabilirsiniz"
          >
            <Dragger {...editUploadProps}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">
                Yeni dosyayı buraya sürükleyin veya tıklayarak seçin
              </p>
              <p className="ant-upload-hint">
                Dosyayı değiştirmek istemiyorsanız bu alanı boş bırakabilirsiniz.
              </p>
            </Dragger>
          </Form.Item>

          <div className="d-flex justify-content-end gap-2">
            <Button onClick={closeEditModal}>
              İptal
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updating}
              icon={<EditOutlined />}
            >
              Belgeyi Güncelle
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Önizleme Modal'ı */}
      <Modal
        open={previewVisible}
        onCancel={handleClosePreview}
        footer={[
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={() => previewFile && handleDownload(previewFile)}>
            İndir
          </Button>,
          <Button key="close" onClick={handleClosePreview}>
            Kapat
          </Button>
        ]}
        width={800}
        centered
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <EyeOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
            <span>{previewFile?.name || 'Dosya Önizleme'}</span>
          </div>
        }
      >
         {previewFile && previewUrl && (
           <div style={{ 
             display: 'flex', 
             justifyContent: 'center', 
             alignItems: 'center',
             width: '100%',
             minHeight: '400px',
             maxHeight: '70vh',
             background: '#f5f5f5',
             borderRadius: '8px',
             padding: '20px',
             overflow: 'auto'
           }}>
             {isImageFile(previewFile.path || previewFile.name) ? (
               <img 
                 src={previewUrl} 
                 alt={previewFile.name} 
                 style={{ 
                   maxWidth: '100%',
                   maxHeight: '65vh',
                   height: 'auto',
                   width: 'auto',
                   objectFit: 'contain',
                   borderRadius: '4px',
                   display: 'block',
                   margin: '0 auto'
                 }} 
               />
             ) : (
               <iframe
                 src={previewUrl}
                 style={{
                   width: '100%',
                   height: '65vh',
                   border: 'none',
                   borderRadius: '4px'
                 }}
                 title={previewFile.name}
               />
             )}
           </div>
         )}
      </Modal>

      <style>
        {`
          .hover-shadow-lg {
            transition: all 0.3s ease;
          }
          .hover-shadow-lg:hover {
            transform: translateY(-3px);
            box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.1) !important;
          }
          .hover-lift {
            transition: all 0.2s ease;
          }
          .hover-lift:hover {
            transform: translateY(-1px);
          }
          .document-icon-wrapper {
            min-height: 80px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .document-icon-wrapper i {
            font-size: 2.5rem;
          }
          .card {
            border: none;
            background: #fff;
            border-radius: 10px;
          }
          .card:hover {
            border-color: #e0e0e0;
          }
          .text-truncate {
            max-width: 150px;
            display: inline-block;
          }
          .action-buttons-grid {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 4px;
            margin-top: 8px;
            padding: 8px;
            background: #f8f9fa;
            border-radius: 8px;
          }
          .action-buttons-grid .ant-btn {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
          }
          .action-buttons-grid .ant-btn:hover {
            background: #fff;
            transform: scale(1.1);
          }
        `}
      </style>

    </div>
  );
};

export default EmployeesDocuments;
